import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { email });
      toast.success("You're on the list. Ohio childcare insights, monthly.");
      setEmail("");
    } catch (err) {
      console.error(err);
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-stone-950 text-stone-400 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5">
              <img src="/logo.svg" alt="ChildCheckOh" className="w-10 h-10" />
              <span className="font-display text-2xl font-semibold tracking-tight text-stone-50">
                ChildCheck <span className="text-emerald-400">Ohio</span>
              </span>
            </div>
            <p className="mt-4 text-sm max-w-sm leading-relaxed">
              Independent operating software for Ohio-licensed childcare centers. Built by operators, designed around OAC 5101:2-12 — audit-ready by default.
            </p>
            <form onSubmit={subscribe} data-testid="newsletter-form" className="mt-6 flex gap-2 max-w-sm">
              <Input
                data-testid="newsletter-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@yourcenter.com"
                className="bg-stone-900 border-stone-700 text-stone-50 placeholder:text-stone-500 h-11"
                required
              />
              <Button
                type="submit"
                data-testid="newsletter-submit-btn"
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-500 rounded-xl px-5 h-11"
              >
                {loading ? "…" : "Subscribe"}
              </Button>
            </form>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
            <FooterCol title="Product" links={[
              { label: "Features", href: "#features" },
              { label: "Product tour", href: "#product" },
              { label: "Pricing", href: "/pricing" },
              { label: "Book a demo", href: "#demo" },
            ]} />
            <FooterCol title="Ohio" links={[
              { label: "DCY Forms", href: "https://childrenandyouth.ohio.gov/forms" },
              { label: "DCY rules & resources", href: "https://childrenandyouth.ohio.gov/wps/portal/gov/dcy/for-providers/rules-and-resources/02-licensing-and-certification" },
              { label: "OAC 5101:2-12", href: "https://www.law.cornell.edu/regulations/ohio/title-5101-2/chapter-5101-2-12" },
              { label: "Compliance module", href: "#ohio-compliance" },
            ]} />
            <FooterCol title="Company" links={[
              { label: "About", href: "#" },
              { label: "Customers", href: "#" },
              { label: "Careers", href: "#" },
              { label: "Email us", href: "mailto:info@childcheckoh.com" },
            ]} />
            <FooterCol title="Legal" links={[
              { label: "Terms of Service", href: "/terms" },
              { label: "Privacy Policy", href: "/privacy" },
              { label: "Refund Policy", href: "/refund" },
              { label: "Pricing", href: "/pricing" },
            ]} />
          </div>
        </div>

        {/* Prominent independence disclaimer — directly addresses user/state legal concerns. */}
        <div
          data-testid="footer-disclaimer"
          className="mt-12 rounded-2xl border border-amber-900/40 bg-amber-950/30 px-5 py-4 text-[12px] leading-relaxed text-amber-100/80"
        >
          <strong className="text-amber-100">Independent software notice.</strong> ChildCheckOh is
          privately-owned operating software for licensed Ohio childcare providers. We are
          <strong className="text-amber-100"> not affiliated with, endorsed by, or funded by</strong> the State of Ohio,
          the Ohio Department of Children &amp;
          Youth (DCY), or any other government agency. References to DCY forms, OAC chapters, and Ohio Early
          Learning &amp; Development Standards are made solely so directors can keep their own licensed
          programs in compliance. Our use of public form numbers (e.g., DCY 01234, DCY 01281) is a factual
          reference under 17 U.S.C. §105 and does not imply endorsement.
        </div>

        <div className="mt-8 pt-8 border-t border-stone-800 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between text-xs text-stone-500">
          <div>© {new Date().getFullYear()} ChildCheckOh · Independent childcare operations software · Columbus, OH · <a href="mailto:info@childcheckoh.com" className="hover:text-stone-200">info@childcheckoh.com</a></div>
          <div className="flex items-center gap-4">
            <a href="https://instagram.com/childcheckoh" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram · @childcheckoh" className="hover:text-stone-200"><i className="fa-brands fa-instagram"></i></a>
            <a href="#" data-testid="social-twitter" aria-label="Twitter" className="hover:text-stone-200"><i className="fa-brands fa-x-twitter"></i></a>
            <a href="#" data-testid="social-linkedin" aria-label="LinkedIn" className="hover:text-stone-200"><i className="fa-brands fa-linkedin"></i></a>
            <a href="#" data-testid="social-youtube" aria-label="YouTube" className="hover:text-stone-200"><i className="fa-brands fa-youtube"></i></a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <div className="text-xs uppercase tracking-[0.2em] font-bold text-stone-200">{title}</div>
    <ul className="mt-4 space-y-2.5">
      {links.map((l) => (
        <li key={l.label}>
          <a
            href={l.href}
            target={l.href.startsWith("http") ? "_blank" : undefined}
            rel={l.href.startsWith("http") ? "noreferrer" : undefined}
            className="text-sm text-stone-400 hover:text-stone-200 transition-colors"
          >
            {l.label}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

export default Footer;
